import { LayerInfo } from "src/common/managers/Types";
import { useContext, useEffect, useRef } from "react";
import { actionSetMapInteractionState, MapInteractionState, MapitStateContext } from "src/states/MapitState";
import { MitLayerHandle } from "src/managers/MapFacade";
import { MitLayerHandleMaplibre } from "src/managers/MapFacadeMaplibre";
import { featureCollection, point } from "@turf/helpers";
import { bearing, centroid, distance, midpoint, transformRotate } from "@turf/turf";
import { MapMouseEvent, Point, LngLat } from "maplibre-gl";
import maplibregl from "maplibre-gl";
import { FeatureCollection, Position } from "geojson";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { createPortal } from "react-dom";
import { Localization } from "@viamap/viamap2-common";

const layerPrefixes = "ImageOverlayEditor"
const UNDO_STACK_MAX = 50;

type Snapshot = {
   geoJson: LayerInfo["geoJson"],
   anchor: [number, number] | undefined,
};

export function ImageOverlayEditor(
   props: {
      layerInfo: LayerInfo,
      onChange: (newLayerInfo: LayerInfo) => void,
   }
) {
   let localLayerHandler = useRef<MitLayerHandle | undefined>(undefined);
   let { state: mapitState, dispatch: mapitDispatch } = useContext(MapitStateContext);
   let localJson = useRef<LayerInfo["geoJson"]>(props.layerInfo.geoJson);
   let lHoverState = useRef<string[]>([])
   let lAnchor = useRef<[number, number] | undefined>(undefined);
   let lDelta = useRef<LayerInfo["geoJson"] | null>(null);
   let svgRef = useRef<SVGSVGElement>(null);
   let activeMoveHandler = useRef<((ev: MapMouseEvent & object) => void) | null>(null);
   let activeEndHandler = useRef<((ev: MapMouseEvent | MouseEvent) => void) | null>(null);


   let originalDataLayerVisibility = useRef<Map<string, string | null | undefined>>(new Map());
   let sourceDataListener = useRef<((ev: any) => void) | null>(null);

   let undoStack = useRef<Snapshot[]>([]);

   function commit() {
      props.onChange({
         ...props.layerInfo,
         geoJson: localJson.current,
      });
   }

   function pushUndoSnapshot() {
      undoStack.current.push({
         geoJson: structuredClone(localJson.current),
         anchor: lAnchor.current ? [lAnchor.current[0], lAnchor.current[1]] : undefined,
      });
      // Keep the base entry, drop oldest non-base entries first.
      while (undoStack.current.length > UNDO_STACK_MAX) {
         undoStack.current.splice(1, 1);
      }
   }

   function applySnapshot(snap: Snapshot) {
      const basicMap = mapitState.map.getMapPop();
      localJson.current = structuredClone(snap.geoJson);
      lAnchor.current = snap.anchor ? [snap.anchor[0], snap.anchor[1]] : undefined;
      lDelta.current = localJson.current;
      const coords = polyToImgCoords(localJson.current);
      try {
         (basicMap.getSource(localLayerHandler.current?.getSourceId() || "") as maplibregl.ImageSource)
            ?.setCoordinates(coords as any);
      } catch { /* source may not be ready */ }
      try {
         (basicMap.getSource("ImageOverlayEditor" + "Source") as maplibregl.GeoJSONSource)
            ?.setData(featureCollection(constructControlData(localJson.current, lAnchor.current) as any));
      } catch { /* source may not be ready */ }
      if (svgRef.current) matchBox(coords, svgRef.current);
   }

   function undo() {
      // Need at least the base + one drag entry to step back.
      if (undoStack.current.length <= 1) return;
      undoStack.current.pop();
      const top = undoStack.current[undoStack.current.length - 1];
      if (top) {
         applySnapshot(top);
         commit();
      }
   }

   function matchBox(imgCoord: [number, number][], svg: SVGSVGElement) {
      svg.style.position = "absolute"
      svg.style.width = "100%"
      svg.style.height = "100%"

      svg.style.pointerEvents = "none";
      svg.innerHTML = "<path class='image-overlay-highlight' d='M " + imgCoord.map(c => {
         let xy = mapitState.map.getMapPop().project(c)
         return xy.x + " " + xy.y
      }).join(" L ") + " Z' />";
   }

   useEffect(() => {
      let basicMap = mapitState.map.getMapPop();


      const editorSourceId = `image-overlay-editor-${props.layerInfo.layerId}`;
      const editorLayerId = `${editorSourceId}-raster`;
      const layerHandle = new MitLayerHandleMaplibre();
      const initialCoords = (props.layerInfo.geoJson.features[0].geometry.coordinates[0] as [number, number][])
         .slice(0, 4) as any;
      layerHandle.addSource(editorSourceId, {
         type: "image",
         url: props.layerInfo.geoJson.features[0].properties._imgUrl as string,
         coordinates: initialCoords,
      });
      layerHandle.addLayer({
         id: editorLayerId,
         type: "raster",
         source: editorSourceId,
         paint: {
            "raster-opacity": props.layerInfo.styling.opacity ?? 0.65,
         },
      });
      localLayerHandler.current = layerHandle;
      if (localLayerHandler.current) {
         localLayerHandler.current.addTo(mapitState.map);
         basicMap.addSource("ImageOverlayEditor" + "Source", {
            type: "geojson",
            data: featureCollection(constructControlData(localJson.current) as any),
         })
      }


      const hideDataLayerNow = () => {
         if (props.layerInfo.layerId === -1) return;
         const dataHandle: any = (mapitState.layers as any)[props.layerInfo.layerId]?.handle;
         const dataLayerIds: string[] = dataHandle?.getLayerIds?.() ?? [];
         dataLayerIds.forEach((id) => {
            try {
               if (basicMap.getLayer(id)) {
                  const prev = basicMap.getLayoutProperty(id, "visibility");
                  originalDataLayerVisibility.current.set(id, prev as string | null | undefined);
                  basicMap.setLayoutProperty(id, "visibility", "none");
               }
            } catch { /* layer may not be ready */ }
         });
      };
      let dataLayerHidden = false;
      const tryHideOnSourceReady = () => {
         if (dataLayerHidden) return;
         try {
            if (basicMap.isSourceLoaded?.(editorSourceId)) {
               dataLayerHidden = true;
               basicMap.off("sourcedata", onSourceData);
               hideDataLayerNow();
            }
         } catch { /* no-op */ }
      };
      const onSourceData = (ev: any) => {
         if (ev?.sourceId === editorSourceId) tryHideOnSourceReady();
      };
      basicMap.on("sourcedata", onSourceData);
      // Cover the case where the source is already loaded (cached image).
      tryHideOnSourceReady();
      sourceDataListener.current = onSourceData;

      // Seed the undo stack with the initial state so Ctrl+Z can step back
      // to "where we started this edit session".
      undoStack.current = [{
         geoJson: structuredClone(localJson.current),
         anchor: undefined,
      }];

      constructControlLayers("ImageOverlayEditor" + "Source").forEach(layer => {
         basicMap.addLayer(layer);
         basicMap.on("mouseenter", layer.id, () => {
            basicMap.getCanvas().style.cursor = "crosshair";
            lHoverState.current.push("move");
         });
         basicMap.on("mouseleave", layer.id, () => {
            lHoverState.current.pop();
            if (lHoverState.current.length === 0) {
               basicMap.getCanvas().style.cursor = "";
            }
         });
      });
      const mouseDown = (e: MouseEvent & MapMouseEvent) => {
         let startPos = e.lngLat;
         if (e.originalEvent.buttons > 3) return; // ignore right click or middle click
         let isLeftButton = (e.originalEvent.buttons === 1)
         e.target.queryRenderedFeatures(e.point).forEach((feature: any) => {
            if (feature.layer.id.startsWith(layerPrefixes)) {
               if (e._defaultPrevented) return;
               e.preventDefault();
               let editType = !isLeftButton ? "scale" : "drag";
               let initial = localJson.current;
               let pivot = structuredClone(lAnchor.current) ?? centroid(initial).geometry.coordinates;
               if (editType === "drag") {
                  lAnchor.current = undefined;
               }

               let isDragging = true;
               const move = (ev: MapMouseEvent & object) => {
                  if (!isDragging) return;
                  let deltaTransform = transAnchorForm(editType, startPos, pivot, ev.lngLat, initial);
                  lDelta.current = deltaTransform;
                  if (lAnchor.current && editType === "drag") {
                     lAnchor.current = (transAnchorForm("drag", startPos, pivot, ev.lngLat, featureCollection([point(lAnchor.current)])) as any).features[0].geometry.coordinates as [number, number];
                  }
                  if (svgRef.current) matchBox(polyToImgCoords(lDelta.current), svgRef.current);
                  (basicMap.getSource(localLayerHandler.current?.getSourceId() || "") as maplibregl.ImageSource)?.setCoordinates(polyToImgCoords(deltaTransform) as any);
                  (basicMap.getSource("ImageOverlayEditor" + "Source") as maplibregl.GeoJSONSource)?.setData(featureCollection(constructControlData(deltaTransform, lAnchor.current) as any));
               }

               const endDrag = (ev: MapMouseEvent | MouseEvent) => {
                  if (!isDragging) return;
                  isDragging = false;

                  basicMap.off("mousemove", move);
                  window.removeEventListener("mouseup", endDrag as EventListener);

                  activeMoveHandler.current = null;
                  activeEndHandler.current = null;

                  let finalLngLat: LngLat;
                  if (ev instanceof MouseEvent) {
                     // Mouse released outside map - use last known position
                     const rect = basicMap.getCanvas().getBoundingClientRect();
                     const point = new Point(ev.clientX - rect.left, ev.clientY - rect.top);
                     finalLngLat = basicMap.unproject(point);
                  } else {
                     finalLngLat = ev.lngLat;
                  }

                  let deltaTransform = transAnchorForm(editType, startPos, pivot, finalLngLat, initial);
                  lDelta.current = deltaTransform;
                  if (editType === "drag") {
                     lAnchor.current = [finalLngLat.lng, finalLngLat.lat];
                     (basicMap.getSource("ImageOverlayEditor" + "Source") as maplibregl.GeoJSONSource)?.setData(featureCollection(constructControlData(deltaTransform, lAnchor.current) as any));
                  }
                  localJson.current = deltaTransform;
                  localLayerHandler.current?.updateSource(mapitState.map, { coordinates: polyToImgCoords(deltaTransform) as any });
                  // Auto-commit each completed gesture so the position is
                  // always saved; no explicit "Save" button needed.
                  commit();
                  // One snapshot per completed gesture so Ctrl+Z steps gesture-by-gesture.
                  pushUndoSnapshot();
               }

               activeMoveHandler.current = move;
               activeEndHandler.current = endDrag;

               basicMap.on("mousemove", move);
               basicMap.once("mouseup", endDrag);
               window.addEventListener("mouseup", endDrag as EventListener, { once: true });
            };
         });
      }


      basicMap.on("mousedown", mouseDown);
      return () => {
         if (activeMoveHandler.current) {
            basicMap.off("mousemove", activeMoveHandler.current);
            activeMoveHandler.current = null;
         }
         if (activeEndHandler.current) {
            window.removeEventListener("mouseup", activeEndHandler.current as EventListener);
            activeEndHandler.current = null;
         }

         constructControlLayers("ImageOverlayEditor" + "Source").forEach(layer => {
            if (basicMap.getLayer(layer.id)) basicMap.removeLayer(layer.id);
         });
         basicMap.off("mousedown", mouseDown);
         if (sourceDataListener.current) {
            try { basicMap.off("sourcedata", sourceDataListener.current); } catch { /* no-op */ }
            sourceDataListener.current = null;
         }
         basicMap.getCanvas().style.cursor = "";
         try { basicMap.removeSource("ImageOverlayEditor" + "Source"); } catch { /* already gone */ }

         if (localLayerHandler.current) {
            try { localLayerHandler.current.clearLayers(mapitState.map); } catch { /* no-op */ }
            localLayerHandler.current = undefined;
         }
         originalDataLayerVisibility.current.forEach((vis, id) => {
            try {
               if (basicMap.getLayer(id)) {
                  basicMap.setLayoutProperty(id, "visibility", vis ?? "visible");
               }
            } catch { /* layer may have been removed */ }
         });
         originalDataLayerVisibility.current.clear();
      };
   }, [])

   useEffect(() => {
      mapitDispatch(actionSetMapInteractionState(MapInteractionState.Override));
      return () => {
         mapitDispatch(actionSetMapInteractionState(MapInteractionState.Normal));
      };
   }, []);

   useEffect(() => {

      let map = mapitState.map.getMapPop();
      if (svgRef.current) {
         matchBox(polyToImgCoords(localJson.current), svgRef.current);
      }
      const movMap = () => {
         if (svgRef.current) {
            matchBox(polyToImgCoords(lDelta.current || localJson.current), svgRef.current);
         }
      }
      map.on("move", movMap);
      return () => {
         map.off("move", movMap);
      };
   })

   useEffect(() => {
      const onKeyDown = (e: KeyboardEvent) => {
         if (!(e.ctrlKey || e.metaKey)) return;
         if (e.shiftKey) return;
         if (e.key.toLowerCase() !== "z") return;
         const target = e.target as HTMLElement | null;
         if (target) {
            const tag = target.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) {
               return;
            }
         }
         e.preventDefault();
         undo();
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
   }, []);


   return (<>
      <div style={{ fontSize: "11px", opacity: 0.75, padding: "4px 6px 8px" }}>
         {Localization.getText("Drag the image to move it. Right-click and drag to rotate and scale.")}
         {" "}
         <span style={{ opacity: 0.8 }}>{Localization.getText("Ctrl+Z to undo.")}</span>
      </div>
      {createPortal(
         <>
            <svg style={{ pointerEvents: "none" }} ref={svgRef} />
         </>, document.getElementById("map") || document.body
      )}
   </>
   )
}

function transAnchorForm(
   editType: string,
   startPos: maplibregl.LngLat,
   pivot: Position,
   currentPos: maplibregl.LngLat,
   initial: LayerInfo["geoJson"],
): FeatureCollection {
   let result = structuredClone(initial);
   let distStart = distance([startPos.lng, startPos.lat], [pivot[0], pivot[1]]);
   let distCurrent = distance([currentPos.lng, currentPos.lat], [pivot[0], pivot[1]]);
   let scale = distCurrent / distStart;
   let transLateX = (startPos.lng - currentPos.lng);
   let transLateY = (startPos.lat - currentPos.lat);
   if (editType === "drag") {
      result = GenerateGeomUtils.geoJsonCoordsTransformer<number>(initial, (coord) => {
         return [coord[0] - transLateX, coord[1] - transLateY];
      });
   }
   if (editType.includes("scale")) {
      let angleStart = bearing([pivot[0], pivot[1]], [startPos.lng, startPos.lat]);
      let angleCurrent = bearing([pivot[0], pivot[1]], [currentPos.lng, currentPos.lat]);
      let angleDiff = angleCurrent - angleStart;
      result = transformRotate(initial, angleDiff, { pivot: pivot });
      result = GenerateGeomUtils.geoJsonCoordsTransformer<number>(result, (coord) => {
         return [
            pivot[0] + (coord[0] - pivot[0]) * scale,
            pivot[1] + (coord[1] - pivot[1]) * scale,
         ]
      });
   }
   if (result.features[0].geometry.coordinates?.length !== 1) {
      return result;
   }
   if (result.features[0].geometry.coordinates[0].flat?.().filter((a) => (a !== Infinity)).length !== 10) {
      return initial;
   }
   return result;
}
function polyToImgCoords(featureCollection: any): [number, number][] {
   return featureCollection.features[0].geometry.coordinates[0].slice(0, 4) as [number, number][]
}

function constructControlData(geoJson: LayerInfo["geoJson"], anchor?: [number, number]): GeoJSON.Feature[] {
   return [
      ...geoJson.features.map((a, idx) => ({ ...a, id: idx, properties: { editType: "drag" } })),
      ...geoJson.features[0].geometry.coordinates[0].slice(0, 4).map((coord, idx) => ({
         type: "Feature",
         id: 100 + idx,
         geometry: { type: "Point", coordinates: coord },
         properties: { editType: "scale", cornerIndex: idx }
      })),
      ...geoJson.features[0].geometry.coordinates[0].slice(0, 4).map((coord, idx, arr) => ({
         type: "Feature",
         id: 200 + idx,
         geometry: { type: "Point", coordinates: midpoint(coord, arr[(idx + 1) % arr.length])!.geometry.coordinates },
         properties: { editType: "rotate", edgeIndex: idx }
      })),
      ...(anchor ? [{
         type: "Feature",
         id: 300,
         geometry: { type: "Point", coordinates: anchor },
         properties: { editType: "anchor" }
      }] : [])
   ]
}


function constructControlLayers(sourceId: string): maplibregl.LayerSpecification[] {
   return [
      {
         id: layerPrefixes + "DragLayer",
         type: "fill",
         source: sourceId,
         filter: ["==", ["get", "editType"], "drag"],
         paint: {
            "fill-color": "#d6c251",
            "fill-opacity": 0.0,
            "fill-outline-color": "#d6c251",
         },
      },
      {
         id: layerPrefixes + "AnchorLayer",
         type: "symbol",
         source: sourceId,
         filter: ["==", ["get", "editType"], "anchor"],
         layout: {
            "icon-image": "mit-patt-char-📍",
            "icon-size": 1.8,
            "icon-anchor": "bottom",
            "icon-offset": [0, 9],
         }

      }
   ];
}
